import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";

/** Where the selected item sits inside its row, in the row's own pixels. */
interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The two shapes a gliding mark takes. Only `transform` ever moves — never a
 * width or a left offset (global constraint: animate transform and opacity
 * only).
 * - `tab`: a bar under the tab row, one pixel wide and *scaled* to the
 *   selected tab — across and no further. A flat bar does not mind the scale.
 * - `radio`: the knob behind the checked radio of a segmented group (design
 *   2026-10-24 §4.2). It has a corner and a shadow, which a scale would
 *   stretch, so it is *sized* to the checked radio and only translated; a
 *   change of choice is a FLIP (see `place`). The group may wrap onto a second
 *   line, so it also finds its way down.
 * - `pressed`: that knob behind the pressed one of a row of toggles.
 */
const KINDS = {
  tab: {
    item: '[role="tab"]',
    selected: '[role="tab"][aria-selected="true"]',
    sized: false,
    transform: ({ x, w }: Box) => `translateX(${x}px) scaleX(${w})`,
  },
  radio: {
    item: '[role="radio"]',
    selected: '[role="radio"][aria-checked="true"]',
    sized: true,
    transform: ({ x, y }: Box) => `translate(${x}px, ${y}px)`,
  },
  // The same knob behind a row of toggles (`aria-pressed`) of which one is
  // pressed at a time — the record's filter (design 2026-10-24 §6.4): each a
  // Tab stop of its own, so not a radio group, but one knob all the same.
  pressed: {
    item: "[aria-pressed]",
    selected: '[aria-pressed="true"]',
    sized: true,
    transform: ({ x, y }: Box) => `translate(${x}px, ${y}px)`,
  },
} as const;

export type GlideKind = keyof typeof KINDS;

/** Where each sized mark rests now — the start of its next FLIP. */
const resting = new WeakMap<HTMLElement, Box>();

/**
 * Where a sized mark shows right now: mid-glide that is not where it was
 * last sent but somewhere on the way, and a choice changed again must start
 * from there or it would jump back first. The browser answers with the
 * running transform as a `matrix(…)`; anything else (jsdom answers with the
 * inline value) falls back to where it was sent.
 */
function shownBox(mark: HTMLElement, sent: Box): Box {
  const matrix = /^matrix\(([^)]+)\)$/.exec(getComputedStyle(mark).transform);
  if (matrix === null) return sent;
  const [a, , , d, e, f] = matrix[1]!.split(",").map(Number);
  const w = parseFloat(mark.style.inlineSize);
  const h = parseFloat(mark.style.blockSize);
  const box = { x: e!, y: f!, w: a! * w, h: d! * h };
  return Object.values(box).every(Number.isFinite) ? box : sent;
}

/** Arms the transition only after `write` has been committed: reading
 * `offsetWidth` is the forced style flush; without it the browser would fold
 * the written position and the armed transition into one and slide. */
function writeUnarmed(mark: HTMLElement, write: () => void): void {
  delete mark.dataset.ready;
  write();
  void mark.offsetWidth;
  mark.dataset.ready = "";
}

/**
 * Puts the mark where the selected item is. `glide` animates there once the
 * mark has been placed for the first time; `jump` never animates — a font
 * arriving or the row changing width is no reason for a slide, and the first
 * placement must not slide in from the corner.
 *
 * A sized mark glides as a FLIP: it takes the new size at once, is scaled
 * back to the old size at the old place without a transition, and then
 * glides home to `scale(1)`. While it travels its corner is stretched by the
 * ratio of the two widths; where it rests it is true.
 */
function place(root: HTMLElement | null, mark: HTMLElement | null, kind: GlideKind, how: "glide" | "jump") {
  if (root === null || mark === null) return;
  const shape = KINDS[kind];
  const target = root.querySelector<HTMLElement>(shape.selected);
  mark.hidden = target === null;
  if (target === null) return;
  // Offsets, not bounding boxes: a dialog that is still rising in carries a
  // transform, and a bounding box would measure that too. They are relative
  // to the row's padding edge, which is where the mark's `left: 0` / `top: 0`
  // is — the row is `position: relative` for that reason.
  const box: Box = { x: target.offsetLeft, y: target.offsetTop, w: target.offsetWidth, h: target.offsetHeight };
  const next = shape.transform(box);
  const armed = mark.dataset.ready !== undefined;

  if (!shape.sized) {
    if (mark.style.transform === next && armed) return;
    if (how === "glide" && armed) {
      mark.style.transform = next;
      return;
    }
    writeUnarmed(mark, () => {
      mark.style.transform = next;
    });
    return;
  }

  const last = resting.get(mark);
  if (armed && last !== undefined && last.x === box.x && last.y === box.y && last.w === box.w && last.h === box.h) {
    return;
  }
  resting.set(mark, box);
  const size = () => {
    mark.style.inlineSize = `${box.w}px`;
    mark.style.blockSize = `${box.h}px`;
  };
  // A box of no size (nothing laid out yet) has no ratio to scale from.
  if (how === "glide" && armed && last !== undefined && box.w > 0 && box.h > 0) {
    const from = shownBox(mark, last);
    writeUnarmed(mark, () => {
      size();
      mark.style.transform = `${shape.transform(from)} scale(${from.w / box.w}, ${from.h / box.h})`;
    });
    mark.style.transform = next;
    return;
  }
  writeUnarmed(mark, () => {
    size();
    mark.style.transform = next;
  });
}

/**
 * A mark that glides to the selected item of a row of tabs or radios.
 *
 * Measured in a layout effect, so the first paint already has the mark in
 * place and a change of selection — or of a label, which changes its width —
 * moves it before the next frame. Only then: a render that changes neither
 * the selected item (`selected`) nor the row's words measures nothing — a
 * note typed in the record re-renders its tabs and filters on every key, and
 * every measure is a forced layout (final review, M-2). The words are read,
 * not measured: `textContent` lays nothing out. It is measured again,
 * without gliding, whenever the row or one of its items changes size
 * (`ResizeObserver`, where the browser has one; jsdom has none and the
 * render-time measure is all there is). StrictMode runs the effects twice:
 * the second pass finds the mark already where it belongs and writes nothing.
 *
 * `itemCount` is only there so the observer is subscribed again when items
 * come or go.
 */
export function useGlide(
  kind: GlideKind,
  rootRef: RefObject<HTMLElement | null>,
  markRef: RefObject<HTMLElement | null>,
  itemCount: number,
  selected: string,
): void {
  // What the last measure saw: the selected item and the row's words.
  const measured = useRef<string | null>(null);
  useLayoutEffect(() => {
    const seen = `${kind}\u0000${selected}\u0000${rootRef.current?.textContent ?? ""}`;
    if (seen === measured.current && markRef.current?.dataset.ready !== undefined) return;
    measured.current = seen;
    place(rootRef.current, markRef.current, kind, "glide");
  });

  useEffect(() => {
    const root = rootRef.current;
    if (root === null || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => place(rootRef.current, markRef.current, kind, "jump"));
    observer.observe(root);
    root.querySelectorAll(KINDS[kind].item).forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [kind, rootRef, markRef, itemCount]);
}
